import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Fade,
  Avatar,
  Chip
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AppleIcon from '@mui/icons-material/Apple';
import { colors } from 'components/Theme/WebTheme.js';
import { FONT_FAMILY } from '../../common/sharedFunctions.js';

const CLOUD_FUNCTION_URL = '/chatbot';

const useStyles = makeStyles((theme) => ({
  chatButton: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
    color: '#000000',
    boxShadow: '0 8px 24px rgba(212, 175, 55, 0.4)',
    zIndex: 1000,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: '0 12px 32px rgba(212, 175, 55, 0.6)',
    },
    '@media (max-width: 600px)': {
      bottom: '20px',
      right: '20px',
      width: '56px',
      height: '56px',
    },
  },
  chatWindow: {
    position: 'fixed',
    bottom: '100px',
    right: '24px',
    width: '400px',
    maxWidth: 'calc(100vw - 48px)',
    height: '600px',
    maxHeight: 'calc(100vh - 180px)',
    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.8)',
    border: `1px solid ${colors.Button_Primary}`,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    overflow: 'hidden',
    '@media (max-width: 600px)': {
      bottom: '80px',
      right: '16px',
      left: '16px',
      width: 'auto',
      maxWidth: 'none',
      height: '500px',
      maxHeight: 'calc(100vh - 200px)',
    },
  },
  chatHeader: {
    background: `linear-gradient(135deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '16px 16px 0 0',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.Button_Primary,
      borderRadius: '4px',
    },
  },
  messageBox: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  userMessage: {
    flexDirection: 'row-reverse',
  },
  messageBubble: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '12px',
    fontFamily: FONT_FAMILY,
    fontSize: '14px',
    lineHeight: '1.5',
  },
  userBubble: {
    background: `linear-gradient(135deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
    color: '#000000',
    borderBottomRightRadius: '4px',
  },
  botBubble: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    borderBottomLeftRadius: '4px',
  },
  inputContainer: {
    padding: '16px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderTop: '1px solid rgba(212, 175, 55, 0.2)',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      fontFamily: FONT_FAMILY,
      borderRadius: '24px',
      background: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: colors.Button_Primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.Button_Primary,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.5)',
      fontFamily: FONT_FAMILY,
    },
  },
  quickAction: {
    margin: '4px',
    background: 'rgba(212, 175, 55, 0.1)',
    color: colors.Button_Primary,
    border: `1px solid ${colors.Button_Primary}`,
    fontFamily: FONT_FAMILY,
    fontSize: '12px',
    '&:hover': {
      background: colors.Button_Primary,
      color: '#000000',
    },
  },
  avatar: {
    width: '32px',
    height: '32px',
  },
}));

const KNOWLEDGE_BASE = {
  company: "Allô Driver",
  assistantName: "Gaëlle",
  slogan: "VTC local avec réservation depuis l’application",
  description: "Service de VTC, livraison Eats et accès restaurateurs avec estimation avant validation, suivi et gestion documentaire.",
  onboarding: {
    driverVtc: {
      title: "Inscription chauffeur VTC",
      path: "Créer un compte dans l’application, choisir le profil chauffeur, compléter le dossier et attendre la validation admin.",
      documents: ["Permis de conduire", "Carte professionnelle VTC", "Pièce d’identité", "Assurance professionnelle", "Carte grise", "Photo du véhicule", "RIB ou informations de paiement selon configuration"]
    },
    courierEat: {
      title: "Inscription livreur Eats",
      path: "Créer un compte, demander l’activation livreur Eats, renseigner la zone de livraison et transmettre les documents demandés.",
      documents: ["Pièce d’identité", "Téléphone joignable", "Mode de transport", "Assurance si véhicule motorisé", "RIB ou informations de paiement selon configuration"]
    },
    restaurant: {
      title: "Accès restaurateur",
      path: "Faire une demande depuis la page restaurateurs. Après vérification, l’équipe crée un accès dédié : 1 accès = 1 restaurant.",
      documents: ["Nom du restaurant", "Responsable", "Email", "Téléphone", "Ville et adresse", "SIRET si disponible", "Type de cuisine", "Documents administratifs si demandés par l’équipe"]
    }
  },
  
  services: [
    "Réservation de VTC via application mobile",
    "Véhicules hybrides et électriques selon disponibilité",
    "Transferts aéroport Toulouse-Blagnac avec suivi de vol temps réel",
    "Transferts gare (Toulouse-Matabiau, Montauban)",
    "Déplacements professionnels et événements",
    "Trajets toutes distances (urbains et inter-régionaux)",
    "Chauffeurs professionnels avec documents vérifiés",
    "Paiement sécurisé dans l'app (CB, Apple Pay, Google Pay)",
    "Suivi GPS temps réel du chauffeur",
    "Réservation immédiate ou planifiée selon disponibilité chauffeur"
  ],
  
  tarifs: {
    description: "Le tarif est estimé avant validation selon le trajet, le véhicule et les paramètres disponibles dans l’application.",
    avantages: [
      "Estimation visible avant confirmation",
      "Informations de prix affichées clairement",
      "Tarifs affichés clairement avant validation",
      "Pas de frais cachés",
      "Factures électroniques automatiques pour notes de frais"
    ]
  },
  
  zones: {
    principales: ["Toulouse", "Montauban", "Blagnac", "Colomiers", "Balma", "L'Union"],
    couverture: "Montauban et toute la région Occitanie, avec service vers tous les principaux aéroports (Toulouse-Blagnac, Carcassonne, etc.)",
    description: "Service VTC proposé dans les zones couvertes, selon disponibilité chauffeur"
  },
  
  flotte: {
    types: [
      "Tesla Model 3 & Model S selon disponibilité",
      "Toyota Camry Hybride (confort et efficacité)",
      "Mercedes EQE (luxe électrique)",
      "Vans électriques 6-7 places pour groupes"
    ],
    equipements: [
      "Climatisation automatique",
      "WiFi gratuit à bord",
      "Chargeurs USB-C et Lightning",
      "Équipements selon véhicule",
      "Espace bagages généreux",
      "Silence de roulement (véhicules électriques)"
    ]
  },
  
  rse: {
    engagement: "Nous privilégions les véhicules hybrides et électriques lorsque cela est possible.",
    impact: [
      "🌱 Véhicules hybrides et électriques selon disponibilité",
      "♻️ Démarche de réduction des émissions",
      "🔋 Suivi de l'évolution de la flotte",
      "🌍 Mobilité locale plus responsable"
    ],
    partenariat: "Partenariat avec Eden Auto Montauban pour des offres véhicules et entretien"
  },
  
  app: {
    fonctionnalites: [
      "Demande de course depuis l’application",
      "Estimation du tarif avant validation",
      "Suivi GPS temps réel du chauffeur",
      "Notifications push à chaque étape",
      "Communication directe avec le chauffeur (message/appel)",
      "Historique des courses",
      "Paiement sécurisé intégré (CB, Apple Pay, Google Pay)",
      "Factures électroniques automatiques",
      "Réservation immédiate ou planifiée",
      "Choix du type de véhicule éco"
    ],
    disponibilite: "Application gratuite disponible sur iOS (App Store) et Android (Google Play)"
  },
  
  partenaires: [
    "Eden Auto Montauban - Concessionnaire Toyota Business (entretien flotte) - 10% de remise",
    "MMA Montauban - Assurance & Protection (RC PRO, Assurance véhicule, Mutuelle santé, Habitation, Retraite) - 10% de remise",
    "Still-inov Agency - Partenaire technologique et marketing",
    "Toulouse-Blagnac Airport - Partenaire transferts aéroport"
  ],
  
  contact: {
    whatsapp: "+33669675282",
    email: "contact@allôdriver.fr",
    phone: "+33 6 64 68 00 61",
    website: "https://allodriver-9139a.web.app"
  },
  
  apps: {
    android: "https://play.google.com/store/apps/details?id=com.allodriver.app",
    ios: "https://apps.apple.com/fr/app/allô-driver-vtc/id6752807639"
  },
  
  horaires: "Service disponible 24h/24, 7j/7, 365 jours par an",
  
  reservations: {
    methodes: [
      "Application mobile (iOS/Android) - Recommandé",
      "Site web https://allodriver-9139a.web.app",
      "Téléphone : +33 6 64 68 00 61",
      "Email : contact@allôdriver.fr"
    ],
    delais: "Réservation immédiate ou planifiée jusqu'à 30 jours à l'avance"
  }
};

export default function Chatbot() {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: `Bonjour, je suis ${KNOWLEDGE_BASE.assistantName}, l'assistante virtuelle d'${KNOWLEDGE_BASE.company}.\n\n${KNOWLEDGE_BASE.slogan}\n\nComment puis-je vous aider aujourd'hui ?\n\nVous pouvez me poser des questions sur :\n• Nos services VTC\n• Tarifs et réservations\n• Télécharger l'application\n• Devenir chauffeur VTC\n• Devenir livreur Eats\n• Demander un accès restaurateur\n• Documents à fournir\n• Nous contacter`,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const openGaelle = (event) => {
      const prompt = event.detail?.prompt || '';
      setIsOpen(true);
      if (prompt) setInput(prompt);
    };
    window.addEventListener('open-gaelle-chat', openGaelle);
    return () => window.removeEventListener('open-gaelle-chat', openGaelle);
  }, []);

  const generateBotResponse = (userMessage) => {
    const messageLower = userMessage.toLowerCase();
    
    // Salutations
    if (messageLower.match(/^(salut|bonjour|hello|hi|hey|bonsoir)$/)) {
      return `Bonjour, je suis ${KNOWLEDGE_BASE.assistantName}. Bienvenue chez ${KNOWLEDGE_BASE.company}.\n\nComment puis-je vous aider aujourd'hui ?\n• Informations sur nos services\n• Télécharger l'application\n• Tarifs et réservations\n• Inscription chauffeur VTC\n• Inscription livreur Eats\n• Accès restaurateur\n• Documents à fournir\n• Nous contacter`;
    }

    if (messageLower.includes('inscription') || messageLower.includes('devenir') || messageLower.includes('document') || messageLower.includes('dossier') || messageLower.includes('restaurateur') || messageLower.includes('restaurant') || messageLower.includes('livreur') || messageLower.includes('eats') || messageLower.includes('vtc')) {
      const isRestaurant = messageLower.includes('restaurateur') || messageLower.includes('restaurant');
      const isCourier = messageLower.includes('livreur') || messageLower.includes('eats') || messageLower.includes('livraison');
      const isVtcDriver = messageLower.includes('chauffeur') || messageLower.includes('vtc') || messageLower.includes('driver');
      const blocks = [];

      if (isVtcDriver || (!isRestaurant && !isCourier)) blocks.push(KNOWLEDGE_BASE.onboarding.driverVtc);
      if (isCourier || (!isRestaurant && !isVtcDriver)) blocks.push(KNOWLEDGE_BASE.onboarding.courierEat);
      if (isRestaurant || (!isCourier && !isVtcDriver)) blocks.push(KNOWLEDGE_BASE.onboarding.restaurant);

      return `Je peux vous guider pour l’inscription et les documents.\n\n${blocks.map(block => `**${block.title}**\n${block.path}\nDocuments généralement demandés :\n${block.documents.map(doc => `• ${doc}`).join('\n')}`).join('\n\n')}\n\nUn dossier est ensuite vérifié par l’équipe Allô Driver avant activation. Pour un cas précis, laissez votre nom, téléphone, ville et le type d’accès souhaité.`;
    }
    
    // Questions sur les tarifs
    if (messageLower.includes('tarif') || messageLower.includes('prix') || messageLower.includes('coût') || messageLower.includes('cout') || messageLower.includes('combien')) {
      return `💰 **Tarifs et estimation**\n\n${KNOWLEDGE_BASE.tarifs.description}\n\n✅ Points utiles :\n${KNOWLEDGE_BASE.tarifs.avantages.map(a => `• ${a}`).join('\n')}\n\n📱 Téléchargez l'app pour consulter une estimation avant validation.\n\n📧 Contact : ${KNOWLEDGE_BASE.contact.email}\n📞 Tél : ${KNOWLEDGE_BASE.contact.phone}`;
    }
    
    // Questions sur les services
    if (messageLower.includes('service') || messageLower.includes('proposez') || messageLower.includes('offrez') || messageLower.includes('faire')) {
      return `🚗 **Nos services VTC**\n\n${KNOWLEDGE_BASE.services.map((s, i) => `${i+1}. ${s}`).join('\n')}\n\n${KNOWLEDGE_BASE.horaires}\n\n💡 Demandez votre course depuis l'application mobile.`;
    }
    
    // Questions sur la zone / couverture
    if (messageLower.includes('zone') || messageLower.includes('région') || messageLower.includes('où') || messageLower.includes('ou') || messageLower.includes('secteur') || messageLower.includes('ville')) {
      return `📍 **Zones de Service**\n\nVilles principales :\n${KNOWLEDGE_BASE.zones.principales.map(z => `• ${z}`).join('\n')}\n\n🌍 Couverture complète :\n${KNOWLEDGE_BASE.zones.couverture}\n\n${KNOWLEDGE_BASE.zones.description}\n\nBesoin d'un trajet spécifique ? Contactez-nous au ${KNOWLEDGE_BASE.contact.phone}`;
    }
    
    // Questions RSE / écologie / environnement
    if (messageLower.includes('écolo') || messageLower.includes('environnement') || messageLower.includes('rse') || messageLower.includes('vert') || messageLower.includes('electrique') || messageLower.includes('électrique') || messageLower.includes('hybrid') || messageLower.includes('emission') || messageLower.includes('planète')) {
      return `🌱 **Démarche mobilité responsable**\n\n${KNOWLEDGE_BASE.rse.engagement}\n\n${KNOWLEDGE_BASE.rse.impact.join('\n')}\n\n🤝 ${KNOWLEDGE_BASE.rse.partenariat}`;
    }
    
    // Questions sur la flotte / véhicules
    if (messageLower.includes('flotte') || messageLower.includes('véhicule') || messageLower.includes('voiture') || messageLower.includes('tesla') || messageLower.includes('toyota') || messageLower.includes('mercedes') || messageLower.includes('van')) {
      return `🚙 **Véhicules disponibles**\n\nSelon la zone et les chauffeurs actifs, la flotte peut inclure :\n${KNOWLEDGE_BASE.flotte.types.map(t => `• ${t}`).join('\n')}\n\nÉquipements possibles :\n${KNOWLEDGE_BASE.flotte.equipements.map(e => `• ${e}`).join('\n')}`;
    }
    
    // Questions sur l'application
    if (messageLower.includes('app') || messageLower.includes('télécharger') || messageLower.includes('telecharger') || messageLower.includes('installer') || messageLower.includes('mobile') || messageLower.includes('application')) {
      return `📱 **Application Mobile Allô Driver**\n\n${KNOWLEDGE_BASE.app.disponibilite}\n\n⚡ Fonctionnalités :\n${KNOWLEDGE_BASE.app.fonctionnalites.slice(0, 6).map(f => `• ${f}`).join('\n')}\n\nCliquez sur les boutons iOS/Android ci-dessous pour télécharger ! ⬇️`;
    }
    
    // Questions fonctionnement app
    if (messageLower.includes('fonctionne') || messageLower.includes('utilise') || messageLower.includes('marche') || messageLower.includes('comment')) {
      return `📲 **Comment ça marche ?**\n\n1️⃣ Téléchargez l'app iOS/Android\n2️⃣ Entrez votre départ et destination\n3️⃣ Consultez l'estimation\n4️⃣ Choisissez le type de véhicule disponible\n5️⃣ Confirmez ou planifiez selon disponibilité\n6️⃣ Suivez les informations de course\n7️⃣ Payez dans l'app selon les moyens activés\n8️⃣ Consultez la facture quand la course est finalisée`;
    }
    
    // Questions sur réservation
    if (messageLower.includes('réserv') || messageLower.includes('book') || messageLower.includes('commander') || messageLower.includes('prendre')) {
      return `📅 **Réserver une course**\n\nMéthodes de réservation :\n${KNOWLEDGE_BASE.reservations.methodes.map(m => `• ${m}`).join('\n')}\n\n⏱️ ${KNOWLEDGE_BASE.reservations.delais}\n\n📱 L'application mobile permet de demander une course et de suivre son état.`;
    }
    
    // Questions aéroport
    if (messageLower.includes('aéroport') || messageLower.includes('aeroport') || messageLower.includes('blagnac') || messageLower.includes('airport') || messageLower.includes('vol')) {
      return `✈️ **Transferts aéroport**\n\nService vers/depuis l'Aéroport Toulouse-Blagnac :\n• Informations de prise en charge\n• Accueil organisé selon réservation\n• Véhicules selon disponibilité\n• Estimation avant validation\n• Contact chauffeur dans l'application\n\n📱 Vous pouvez demander ou planifier le transfert via l'app.`;
    }
    
    // Questions paiement
    if (messageLower.includes('paie') || messageLower.includes('payer') || messageLower.includes('carte') || messageLower.includes('espèce') || messageLower.includes('apple pay') || messageLower.includes('paypal')) {
      return `💳 **Paiement Sécurisé**\n\nMoyens de paiement acceptés dans l'app :\n• Carte bancaire (Visa, Mastercard, Amex)\n• Apple Pay\n• Google Pay\n\n🔒 Paiement 100% sécurisé (cryptage PCI-DSS)\n📧 Factures électroniques envoyées automatiquement\n💼 Parfait pour notes de frais professionnelles`;
    }
    
    // Problèmes techniques
    if (messageLower.includes('problème') || messageLower.includes('probleme') || messageLower.includes('bug') || messageLower.includes('marche pas') || messageLower.includes('erreur') || messageLower.includes('aide')) {
      return `🛠️ **Support Gaëlle / SAV**\n\nPour toute assistance technique :\n\n💬 WhatsApp : ${KNOWLEDGE_BASE.contact.phone}\n📧 Email : ${KNOWLEDGE_BASE.contact.email}\n📞 Téléphone : ${KNOWLEDGE_BASE.contact.phone}\n\nIndiquez votre nom, téléphone, email et le sujet : inscription, livraison, restauration, course ou document.`;
    }
    
    // Questions remboursement / annulation
    if (messageLower.includes('rembours') || messageLower.includes('annul') || messageLower.includes('remboursement') || messageLower.includes('cancel')) {
      return `🔄 **Remboursements & Annulations**\n\nPour toute demande de remboursement ou annulation, contactez notre service client :\n\n📧 Email : ${KNOWLEDGE_BASE.contact.email}\n📞 Téléphone : ${KNOWLEDGE_BASE.contact.phone}\n💬 WhatsApp : ${KNOWLEDGE_BASE.contact.phone}\n\nNous traiterons votre demande dans les meilleurs délais (sous 24-48h).`;
    }
    
    // Contact général
    if (messageLower.includes('contact') || messageLower.includes('joindre') || messageLower.includes('appeler') || messageLower.includes('téléphone') || messageLower.includes('telephone') || messageLower.includes('email') || messageLower.includes('mail')) {
      return `📞 **Nous Contacter**\n\n📧 Email : ${KNOWLEDGE_BASE.contact.email}\n📞 Téléphone : ${KNOWLEDGE_BASE.contact.phone}\n💬 WhatsApp : ${KNOWLEDGE_BASE.contact.phone}\n🌐 Site web : ${KNOWLEDGE_BASE.contact.website}\n\n⏰ ${KNOWLEDGE_BASE.horaires}\n\nCliquez sur les boutons ci-dessous pour nous contacter rapidement ! ⬇️`;
    }
    
    // Partenaires
    if (messageLower.includes('partenaire') || messageLower.includes('toyota') || messageLower.includes('eden') || messageLower.includes('still-inov')) {
      return `🤝 **Nos Partenaires de Confiance**\n\n${KNOWLEDGE_BASE.partenaires.map(p => `• ${p}`).join('\n')}\n\nCes partenariats nous permettent d'offrir des véhicules premium, éco-responsables et impeccablement entretenus.`;
    }
    
    // Horaires
    if (messageLower.includes('horaire') || messageLower.includes('heure') || messageLower.includes('ouvert') || messageLower.includes('disponible') || messageLower.includes('quand')) {
      return `⏰ **Disponibilité**\n\n${KNOWLEDGE_BASE.horaires}\n\nRéservez quand vous voulez, où vous voulez ! 📱`;
    }
    
    // Chauffeurs
    if (messageLower.includes('chauffeur') || messageLower.includes('conducteur') || messageLower.includes('driver') || messageLower.includes('qui conduit')) {
      return `👨‍✈️ **Nos chauffeurs professionnels**\n\nLes chauffeurs doivent disposer des documents requis et d'un profil validé. Selon les profils, ils peuvent aussi proposer :\n• Expérience VTC\n• Connaissance locale\n• Véhicules hybrides ou électriques\n• Contact direct pendant la course`;
    }
    
    // Merci / satisfaction
    if (messageLower.includes('merci') || messageLower.includes('super') || messageLower.includes('génial') || messageLower.includes('parfait') || messageLower.includes('top')) {
      return `😊 Merci beaucoup ! Nous sommes ravis de pouvoir vous aider.\n\n📱 N'hésitez pas à télécharger notre application pour profiter pleinement de nos services !\n\nÀ très bientôt chez ${KNOWLEDGE_BASE.company} ! 🚗✨`;
    }
    
    // Question générale / par défaut
    return `💡 **Gaëlle peut vous aider sur :**\n\n• Réserver une course VTC\n• Télécharger l’application\n• Devenir chauffeur VTC\n• Devenir livreur Eats\n• Demander un accès restaurateur\n• Comprendre les documents à fournir\n• Contacter le SAV\n\nQue souhaitez-vous savoir ?`;
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      type: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setLoading(true);

    try {
      // Build conversation history for OpenAI context
      const conversationHistory = messages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Call Cloud Function with OpenAI
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userInput,
          conversationHistory: conversationHistory
        })
      });

      const data = await response.json();

      let responseText;
      if (data.success && data.response) {
        responseText = data.response;
      } else {
        throw new Error(data.error || 'Erreur lors de la réponse');
      }

      // Simuler un délai naturel
      setTimeout(() => {
        const botResponse = {
          type: 'bot',
          text: responseText,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
        setLoading(false);
      }, 300);

    } catch (error) {
      console.error('Error generating response:', error);
      const responseText = generateBotResponse(userInput);
      setTimeout(() => {
        const botResponse = {
          type: 'bot',
          text: responseText,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
        setLoading(false);
      }, 300);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'android':
        window.open(KNOWLEDGE_BASE.apps.android, '_blank');
        break;
      case 'ios':
        window.open(KNOWLEDGE_BASE.apps.ios, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${KNOWLEDGE_BASE.contact.whatsapp}?text=Bonjour%20Gaëlle,%20je%20souhaite%20une%20aide%20Allô%20Driver`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${KNOWLEDGE_BASE.contact.email}?subject=Demande%20d'information%20Allô%20Driver`;
        break;
      case 'vtc':
        setInput('Je veux devenir chauffeur VTC, quels documents fournir ?');
        break;
      case 'courier':
        setInput('Je veux devenir livreur Eats, comment faire mon inscription ?');
        break;
      case 'restaurant':
        setInput('Je suis restaurateur, comment demander un accès Allô Driver Eats ?');
        break;
      default:
        break;
    }
  };

  const renderQuickActions = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      <Chip
        label="Chauffeur VTC"
        onClick={() => handleQuickAction('vtc')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        label="Livreur Eats"
        onClick={() => handleQuickAction('courier')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        label="Restaurateur"
        onClick={() => handleQuickAction('restaurant')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        icon={<PhoneAndroidIcon />}
        label="Android"
        onClick={() => handleQuickAction('android')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        icon={<AppleIcon />}
        label="iOS"
        onClick={() => handleQuickAction('ios')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        icon={<WhatsAppIcon />}
        label="WhatsApp"
        onClick={() => handleQuickAction('whatsapp')}
        className={classes.quickAction}
        size="small"
      />
      <Chip
        icon={<EmailIcon />}
        label="Email"
        onClick={() => handleQuickAction('email')}
        className={classes.quickAction}
        size="small"
      />
    </Box>
  );

  return (
    <>
      {/* Chat Button */}
      <IconButton
        className={classes.chatButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <CloseIcon sx={{ fontSize: 28 }} /> : <ChatIcon sx={{ fontSize: 28 }} />}
      </IconButton>

      {/* Chat Window */}
      <Fade in={isOpen}>
        <Paper className={classes.chatWindow} elevation={24}>
          {/* Header */}
          <Box className={classes.chatHeader}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon sx={{ fontSize: 28, color: '#000000' }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 'bold',
                    color: '#000000',
                    fontSize: '16px'
                  }}
                >
                  Gaëlle Allô Driver
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    color: 'rgba(0, 0, 0, 0.7)',
                    fontSize: '12px'
                  }}
                >
                  Inscriptions, SAV et réservations
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: '#000000' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box className={classes.messagesContainer}>
            {messages.map((message, index) => (
              <Box
                key={index}
                className={`${classes.messageBox} ${
                  message.type === 'user' ? classes.userMessage : ''
                }`}
              >
                <Avatar
                  className={classes.avatar}
                  sx={{
                    background:
                      message.type === 'user'
                        ? colors.Button_Primary
                        : 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {message.type === 'user' ? (
                    <PersonIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                  )}
                </Avatar>
                <Box
                  className={`${classes.messageBubble} ${
                    message.type === 'user' ? classes.userBubble : classes.botBubble
                  }`}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'pre-line',
                      fontFamily: FONT_FAMILY,
                      fontSize: '14px'
                    }}
                  >
                    {message.text}
                  </Typography>
                </Box>
              </Box>
            ))}
            {loading && (
              <Box className={classes.messageBox}>
                <Avatar
                  className={classes.avatar}
                  sx={{ background: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <SmartToyIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box className={`${classes.messageBubble} ${classes.botBubble}`}>
                  <CircularProgress size={20} sx={{ color: colors.Button_Primary }} />
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Quick Actions */}
          <Box sx={{ px: 2, pb: 1 }}>
            {renderQuickActions()}
          </Box>

          {/* Input */}
          <Box className={classes.inputContainer}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Posez votre question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                className={classes.textField}
                disabled={loading}
                size="small"
              />
              <IconButton
                onClick={handleSend}
                disabled={!input.trim() || loading}
                sx={{
                  background: `linear-gradient(135deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
                  color: '#000000',
                  '&:hover': {
                    background: `linear-gradient(135deg, #f0c040 0%, ${colors.Button_Primary} 100%)`,
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}
